import {
  composeSDKFactories,
  createCompSchemaValidator,
  assert,
  reportWarning,
  messageTemplates,
  registerCorvidEvent,
  withValidation,
  createElementPropsSDKFactory,
  clickPropsSDKFactory,
  disablePropsSDKFactory,
  toJSONBase,
  changePropsSDKFactory,
  createAccessibilityPropSDKFactory,
} from '@wix/editor-elements-corvid-utils';
import { createComponentSDKModel } from '@wix/editor-elements-integrations/corvid';
import type { $W } from '@wix/thunderbolt-symbols';
import type {
  IPaginationProps,
  IPaginationSDK,
  IPaginationOwnSDKFactory,
  OnNextClickComponentEvent,
  OnNextClickCorvidEvent,
  OnPrevClickCorvidEvent,
  OnPrevClickComponentEvent,
  PaginationMode,
  PaginationSDKNavigationType,
} from '../Pagination.types';
import { isNextEnabled, isPreviousEnabled } from '../utils';

const navigationTypeMap: Record<PaginationSDKNavigationType, PaginationMode> = {
  clickNumbers: 'pages',
  inputNumbers: 'input',
  arrowsOnly: 'arrows',
};

const navigationTypeInvertedMap: Record<
  PaginationMode,
  PaginationSDKNavigationType
> = {
  pages: 'clickNumbers',
  input: 'inputNumbers',
  arrows: 'arrowsOnly',
};

const _paginationSDKFactory: IPaginationOwnSDKFactory = api => {
  const { setProps, props, metaData } = api;
  const schemaValidator = createCompSchemaValidator(metaData.role);

  return {
    get currentPage() {
      if (props.navigationType === 'arrows') {
        reportWarning('currentPage is unavailable when in arrows mode');
      }
      return props.currentPage;
    },

    set currentPage(newCurrentPage) {
      if (
        !schemaValidator(
          newCurrentPage,
          {
            type: ['integer', 'nil'],
            minimum: 1,
            maximum: props.totalPages,
            warnIfNil: true,
          },
          'currentPage',
        )
      ) {
        return;
      }
      setProps({
        currentPage: assert.isNil(newCurrentPage) ? 1 : newCurrentPage,
      });
    },

    get totalPages() {
      if (props.navigationType === 'arrows') {
        reportWarning('currentPage is unavailable when in arrows mode');
      }
      return props.totalPages;
    },

    set totalPages(newTotalPages) {
      if (
        !schemaValidator(
          newTotalPages,
          { type: ['integer'], minimum: 1 },
          'totalPages',
        )
      ) {
        return;
      }
      if (props.currentPage > newTotalPages) {
        reportWarning(
          messageTemplates.warning_value_changed({
            propertyName: 'totalPages',
            compName: metaData.role,
            newValue: String(newTotalPages),
            changedProperty: 'currentPage',
          }),
        );
        setProps({ totalPages: newTotalPages, currentPage: newTotalPages });
      } else {
        setProps({ totalPages: newTotalPages });
      }
    },

    get navigationType() {
      return navigationTypeInvertedMap[props.paginationMode];
    },

    set navigationType(newMode: PaginationSDKNavigationType) {
      const _newMode = navigationTypeMap[newMode];
      setProps({
        ...props.paginationModeProps[_newMode],
        paginationMode: _newMode,
      });
    },

    get nextEnabled() {
      return isNextEnabled({
        forceNextButtonEnabled: props.forceNextButtonEnabled,
        isDisabled: props.isDisabled,
        currentPage: props.currentPage,
        totalPages: props.totalPages,
      });
    },

    set nextEnabled(val: boolean) {
      setProps({
        forceNextButtonEnabled: val,
      });
    },

    get previousEnabled() {
      return isPreviousEnabled({
        forcePreviousButtonEnabled: props.forcePreviousButtonEnabled,
        isDisabled: props.isDisabled,
        currentPage: props.currentPage,
      });
    },

    set previousEnabled(val: boolean) {
      setProps({
        forcePreviousButtonEnabled: val,
      });
    },

    get previousPageUrl() {
      return props.previousPageUrl;
    },

    set previousPageUrl(url: string | undefined | null) {
      setProps({
        previousPageUrl: url ?? undefined,
      });
    },

    get nextPageUrl() {
      return props.nextPageUrl;
    },

    set nextPageUrl(url: string | undefined | null) {
      setProps({
        nextPageUrl: url ?? undefined,
      });
    },

    onNextClicked: (cb: (event: OnNextClickCorvidEvent, $w: $W) => void) =>
      registerCorvidEvent<OnNextClickComponentEvent, OnNextClickCorvidEvent>(
        'onNextClick',
        api,
        cb,
        ({ componentEvent }) => componentEvent,
      ),

    onPreviousClicked: (cb: (event: OnPrevClickCorvidEvent, $w: $W) => void) =>
      registerCorvidEvent<OnPrevClickComponentEvent, OnPrevClickCorvidEvent>(
        'onPrevClick',
        api,
        cb,
        ({ componentEvent }) => componentEvent,
      ),

    toJSON() {
      const { currentPage, totalPages } = props;
      return {
        ...toJSONBase(metaData),
        currentPage,
        totalPages,
      };
    },
  };
};

const paginationSDKFactory = withValidation(_paginationSDKFactory, {
  type: ['object'],
  properties: {
    nextEnabled: {
      type: ['boolean'],
    },
    previousEnabled: {
      type: ['boolean'],
    },
    navigationType: {
      type: ['string'],
      enum: ['clickNumbers', 'inputNumbers', 'arrowsOnly'],
    },
    onNextClicked: {
      type: ['function'],
      args: [{ type: ['function'] }],
    },
    onPreviousClicked: {
      type: ['function'],
      args: [{ type: ['function'] }],
    },
    previousPageUrl: {
      type: ['string', 'nil'],
      maxLength: 2048,
      minLength: 4,
    },
    nextPageUrl: {
      type: ['string', 'nil'],
      maxLength: 2048,
      minLength: 4,
    },
  },
});

export const accessibilityPropsSDKFactory = createAccessibilityPropSDKFactory({
  enableAriaLabel: true,
  enableLang: true,
});

export const sdk = composeSDKFactories<IPaginationProps, IPaginationSDK>([
  createElementPropsSDKFactory(),
  disablePropsSDKFactory,
  clickPropsSDKFactory,
  changePropsSDKFactory,
  paginationSDKFactory,
  accessibilityPropsSDKFactory,
]);

export default createComponentSDKModel(sdk);
